import * as React from 'react';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { STALE_DATA_TIMEOUT } from '@studio/constants/api-constants';
import { useClient } from '@studio/hooks';
import { runCachedAbortableStoreFetch } from '@studio/storeV2/helpers/run-cached-abortable-store-fetch';
import { customerNameOptionsAtom, customerNamesAtom, customerNamesLoadInFlightAtom } from '../../customer';

const useCustomerNames = ({ fetchOnMount = false }: { fetchOnMount?: boolean } = {}) => {
  const customerNamesState = useAtomValue(customerNamesAtom);
  const customerNamesLoadInFlight = useAtomValue(customerNamesLoadInFlightAtom);
  const customerNameOptions = useAtomValue(customerNameOptionsAtom);

  const { callApi: getCustomerNames, isFetching: isClientFetching } = useClient({
    config: useClient.central.v2.customers.names.GET,
  });

  const fetchCustomerNames = useAtomCallback(
    React.useCallback(
      async (get, set, { forceRefresh }: { forceRefresh?: boolean } = {}) => {
        return runCachedAbortableStoreFetch(get, set, {
          dataAtom: customerNamesAtom,
          emptyOnError: null,
          fetchWithSignal: (signal) => getCustomerNames(undefined, { signal }),
          forceRefresh,
          loadInFlightAtom: customerNamesLoadInFlightAtom,
          staleTimeoutMs: STALE_DATA_TIMEOUT,
        });
      },
      [getCustomerNames]
    )
  );

  React.useEffect(() => {
    if (!fetchOnMount) return;
    void fetchCustomerNames();
  }, [fetchOnMount, fetchCustomerNames]);

  const isFetching = customerNamesLoadInFlight != null || isClientFetching;

  const customerNames = React.useMemo(
    () => customerNamesState.data?.map((entry) => entry.name) ?? null,
    [customerNamesState.data]
  );

  return {
    customerNameData: customerNamesState.data,
    customerNameOptions,
    customerNames,
    error: customerNamesState.error,
    fetchCustomerNames,
    hasFetched: customerNamesState.hasFetched,
    isFetching,
  };
};

export default useCustomerNames;
